import React, { FC, memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HousekeepingManagePermissionsConfig } from '@hkm/components/Housekeeping/shared/config/housekeepingManagePermissionsConfig';
import OccupancyDiscrepancyModal from '@hkm/components/shared/OccupancyDiscrepancy/Modal/OccupancyDiscrepancyModal';
import { GuestCount } from '@hkm/shared/interfaces/guestCount';
import { usePermission } from '@hkm/shared/permissions/hooks/usePermission';
import { RoomOccupancy } from '@hkm/shared/roomOccupancy/roomOccupancy';
import classNames from 'classnames';

import {
  AcButton,
  AcButtonPattern,
  AcButtonShape,
  AcButtonSize
} from '@ac/mobile-components/dist/components/button';
import { AcButtonContent } from '@ac/mobile-components/dist/components/button-content';
import { AcFlex } from '@ac/mobile-components/dist/components/flex';
import { AcFormElement } from '@ac/mobile-components/dist/components/form-element';
import { AlignItems, Icon } from '@ac/mobile-components/dist/enums';
import { Testable } from '@ac/mobile-components/dist/interfaces/componentProps';
import { getTestSelectorAttribute } from '@ac/mobile-components/dist/utils';

import './OccupancyDiscrepancyButton.css';

export interface HousekeepingOccupancyProps extends Testable {
  occupancy: RoomOccupancy;
  onSave: (count: GuestCount) => void;
  onDelete: () => void;
}
const OccupancyDiscrepancyButton: FC<HousekeepingOccupancyProps> = ({
  testSelector,
  onDelete,
  onSave,
  occupancy
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setModalOpen] = useState(false);
  const removeAreaRef = useRef<HTMLDivElement>(null);
  const hasManageHousekeepingPermission = usePermission(
    ...HousekeepingManagePermissionsConfig
  );

  const onModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const onButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const target = e.target as HTMLElement;
      if (removeAreaRef.current?.contains(target)) {
        onDelete();
      } else {
        setModalOpen(true);
      }
    },
    [onDelete]
  );

  if (!hasManageHousekeepingPermission && !occupancy.hasReport) {
    return null;
  }

  return (
    <>
      {occupancy.canReport && (
        <div className="occupancy-discrepancy">
          <AcFormElement
            label={t('ROOM_DETAILS.HOUSEKEEPING_OCCUPANCY.LABEL')}
            testSelector={testSelector}
          >
            <AcButton
              onClick={onButtonClick}
              testSelector={`${testSelector}-button`}
              pattern={AcButtonPattern.Light}
              size={AcButtonSize.Small}
              shape={AcButtonShape.Rectangle}
              disabled={!hasManageHousekeepingPermission}
            >
              {occupancy.hasReport ? (
                <>
                  <AcFlex
                    alignItems={AlignItems.center}
                    className={classNames({
                      'occupancy-discrepancy-error':
                        occupancy.hasAdultsDiscrepancy
                    })}
                  >
                    <AcButtonContent
                      icon={
                        occupancy.hasAdultsDiscrepancy
                          ? Icon.Todo
                          : Icon.Default
                      }
                      text={t('GLOBAL.ADULTS', {
                        count: occupancy.reportedAdults
                      })}
                    />
                  </AcFlex>
                  <AcFlex
                    alignItems={AlignItems.center}
                    className={classNames('ac-spacing-left-md', {
                      'occupancy-discrepancy-error':
                        occupancy.hasChildrenDiscrepancy
                    })}
                  >
                    <AcButtonContent
                      icon={
                        occupancy.hasChildrenDiscrepancy
                          ? Icon.Todo
                          : Icon.Default
                      }
                      text={t('GLOBAL.CHILDREN', {
                        count: occupancy.reportedChildren
                      })}
                    />
                  </AcFlex>
                  <AcFlex
                    alignItems={AlignItems.center}
                    className="ac-spacing-left-md"
                  >
                    <AcButtonContent icon={Icon.Cancel} />
                  </AcFlex>
                  <div
                    ref={removeAreaRef}
                    className="occupancy-discrepancy-remove-area"
                    {...getTestSelectorAttribute(testSelector, 'removeArea')}
                  />
                </>
              ) : (
                <AcButtonContent
                  icon={Icon.Plus}
                  text={t('ROOM_DETAILS.HOUSEKEEPING_OCCUPANCY.ADD')}
                />
              )}
            </AcButton>
          </AcFormElement>
          <OccupancyDiscrepancyModal
            open={isModalOpen}
            occupancy={occupancy}
            onClose={onModalClose}
            testSelector={testSelector}
            onSave={onSave}
          />
        </div>
      )}
    </>
  );
};

export default memo(OccupancyDiscrepancyButton);



// WEBPACK FOOTER //
// ./src/components/shared/OccupancyDiscrepancy/OccupancyDiscrepancyButton.tsx